import { Component, EventEmitter, Inject, OnInit, Output, TemplateRef, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { CommonModule } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatIconModule } from "@angular/material/icon";
import { NgSelectModule } from "@ng-select/ng-select";
import { NgxMaterialTimepickerModule } from "ngx-material-timepicker";

import { TripRouteService } from "../../../components/sales/trip/trip.service";
import { VechicleService } from "../../../components/transport/vechicle/vehicle.service";
import { UserMasterService } from "../../../components/routing/sub-routes/sub-route-collaborator/user_master.service";
import { IUpdateTrip } from "../../../components/sales/trip/interfaces/updateTrip";

import VehicleAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/transport/vehicle/vehicle.entity";
import { firstValueFrom } from "rxjs";
import { FormSectionComponent } from "app/shared/components/form-ui/form-section/form-section.component";
import { MastersUserAttributes } from "@albatrosdeveloper/ave-models-package";
import { DateTimeHelper } from "../../../../utils/date-time-helper.class";
import { TripStatus, TRIP_STATUS_LIST, TRIP_STATUS_LABELS } from "../../../../common/enums/trip-status.enum";
import { RoutesDetailByBusinessPartnerVehicleComponent } from "app/modules/components/modals/routes-detail-by-business-partner-vehicle/routes-detail-by-business-partner-vehicle/routes-detail-by-business-partner-vehicle.component";

@Component({
  selector: "app-shared-trip-edit-modal",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatIconModule,
    NgSelectModule,
    NgxMaterialTimepickerModule,
    FormSectionComponent,
    RoutesDetailByBusinessPartnerVehicleComponent
  ],
  templateUrl: "./shared-trip-edit-modal.component.html",
  styleUrl: "./shared-trip-edit-modal.component.scss",
})
export class SharedTripEditModalComponent implements OnInit {
  @Output() tripUpdated = new EventEmitter<void>();

  tripForm: FormGroup;
  loading = false;
  canEditBasicFields = true;
  canEditStatus = true;
  canEditUser = true;
  
  userMasterList: MastersUserAttributes[] = [];
  vechicleListGeneral: VehicleAttributes[] = [];
  statusList = TRIP_STATUS_LIST;

  parameterIdForVehicles: string[] = [];
  parameterBusinessPartnerId: string[] = [];

  @ViewChild("viewRoutesByVehicleIdModal") viewRoutesByVehicleIdModal!: TemplateRef<any>;
  
  dialogRefRoutesByVehicleIdModal: MatDialogRef<any>;

  tripData  = null;
  minDate = new Date();
  minDateEnd: Date;
  minDateLoad: Date;

  typeModal: string = "vehicle";

  constructor(
    public dialogRef: MatDialogRef<SharedTripEditModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { tripId: string },
    private fb: FormBuilder,
    private tripService: TripRouteService,
    private vehicleService: VechicleService,
    private userMasterService: UserMasterService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) {
    this.initializeForm();
  }

  ngOnInit() {
    if (this.data?.tripId) {
      this.loadTripData();
      this.loadSelectOptions();
    }  

    this.tripForm.get('vehicleId')?.valueChanges.subscribe(vehicleId => {
      this.onVehicleSelected(vehicleId);
    });
  }

  onVehicleSelected(vehicleId: string) {
    this.parameterIdForVehicles = [];
    this.parameterIdForVehicles.push(vehicleId);
  }

  openRoutesByVehicleIdModal(): void {
    this.dialogRefRoutesByVehicleIdModal = this.dialog.open(this.viewRoutesByVehicleIdModal, {
      disableClose: true,
      width: '1200px',
      height: '305px'
    });
    this.dialogRefRoutesByVehicleIdModal.afterClosed().subscribe((result) => {
      result
    });
  }

  private initializeForm() {
    this.tripForm = this.fb.group({
      description: [''],
      loadDateTime: [null, Validators.required],
      time: [null, Validators.required],
      dateHourStartTeoric: [null, Validators.required],
      dateHourEndTeoric: [null, Validators.required],
      masterUserId: [null],
      vehicleId: [null],
      statusTripId: [null, Validators.required],
    });

  }

  private async loadTripData() {
    this.loading = true;
    try {
      const res = await firstValueFrom(this.tripService.findOne(this.data.tripId));
      
      this.tripData = res.data;

      this.parameterIdForVehicles = [];
      if(this.tripData.vehicle) this.parameterIdForVehicles.push(this.tripData.vehicle._id.toString());

      this.parameterBusinessPartnerId = [];
      if(this.tripData.user) this.parameterBusinessPartnerId.push(this.tripData.user.business_partner_id.toString());
      
      this.setEditPermissions(this.tripData.statusTripId);
      
      this.populateForm();
      
      this.loading = false;
    } catch (error) {
      this.loading = false;
      
      this.snackBar.open(error.error?.message || 'Error al cargar los datos del viaje', 'Cerrar', {
        duration: 3000
      });
    }
  }

  private setEditPermissions(statusTripId: number) {
    this.canEditBasicFields = statusTripId === TripStatus.ABIERTO;

    this.canEditStatus = statusTripId !== TripStatus.FINALIZADO && 
                        statusTripId !== TripStatus.DOC_LIQUIDACION_CREADO && 
                        statusTripId !== TripStatus.DOC_LIQUIDACION_APROBADO;
    
    this.canEditUser = statusTripId === TripStatus.ABIERTO || statusTripId === TripStatus.CARGANDO;

    if(!this.canEditUser) {
      this.tripForm.get('masterUserId')?.disable();
    }

    if(!this.canEditStatus) {
      this.tripForm.get('statusTripId')?.disable();
    }

    if(!this.canEditBasicFields) {
      this.tripForm.get('description')?.disable();
      this.tripForm.get('loadDateTime')?.disable();
      this.tripForm.get('dateHourStartTeoric')?.disable();
      this.tripForm.get('dateHourEndTeoric')?.disable();
      this.tripForm.get('vehicleId')?.disable();
      this.tripForm.get('time')?.disable();
    }
    
  }
  
  private getStatusName(statusId: number): string {
    return TRIP_STATUS_LABELS[statusId as TripStatus] || 'Desconocido';
  }

  private populateForm() {
    if (!this.tripData) return;

    let timeString = null;
    let loadDateOnly = null;

    if (this.tripData.loadDateTime) {
      const loadDateTime = new Date(this.tripData.loadDateTime);
      
      timeString = DateTimeHelper.extractTimeToAMPM(loadDateTime);
      
      loadDateOnly = DateTimeHelper.extractDateOnly(loadDateTime);
      
    }

    this.tripForm.patchValue({
      description: this.tripData.description || '',
      loadDateTime: loadDateOnly,  
      time: timeString, 
      dateHourStartTeoric: this.tripData.dateHourStartTeoric ? new Date(this.tripData.dateHourStartTeoric) : null,
      dateHourEndTeoric: this.tripData.dateHourEndTeoric ? new Date(this.tripData.dateHourEndTeoric) : null,
      masterUserId: this.tripData.user?._id || null,
      vehicleId: this.tripData.vehicle?._id || null,
      statusTripId: this.tripData.statusTripId || null,
    });

    const startDate = this.tripForm.get('dateHourStartTeoric')?.value;
    if (startDate) {
      this.minDate = new Date(startDate);
      this.minDateEnd = new Date(startDate);
      
      
      this.minDateLoad = new Date(startDate);
      this.minDateLoad.setDate(this.minDateLoad.getDate() - 1);
    }
  }

  private async loadSelectOptions() {
    try {
      await this.fetchAllUserMaster();
      await this.fetchAllVehicles();
    } catch (error) {
      this.snackBar.open(error.error.message || "Error al seleccionar una opción.","Cerrar",{ duration: 5000});
    }
  }

  private async fetchAllUserMaster(): Promise<void> {
    try {
      const filter = { search: "" };
      const resUser = await firstValueFrom(
        this.userMasterService.findUserAvailable(filter)
      );
      this.userMasterList = resUser.data;
    } catch (err) {
      this.snackBar.open(err.error?.message || "Error al cargar la lista de usuarios", "Cerrar", { duration: 3000 });
      this.userMasterList = [];
    }
  }

  private async fetchAllVehicles(): Promise<void> {
    try {
      const res = await firstValueFrom(this.vehicleService.findToComboBox());
      this.vechicleListGeneral = res.data;
    } catch (err) {
      this.snackBar.open(err.error?.message || "Error al cargar la lista de vehículos", "Cerrar", { duration: 3000 });
      this.vechicleListGeneral = [];
    }
  }

  onDateStartChange() {
    const startDate = this.tripForm.get('dateHourStartTeoric')?.value;
    if (startDate) {
      this.minDateEnd = new Date(startDate);
      
      this.minDateLoad = new Date(startDate);
      this.minDateLoad.setDate(this.minDateLoad.getDate() - 1);
      
      const endDate = this.tripForm.get('dateHourEndTeoric')?.value;
      if (endDate && endDate < startDate) {
        this.tripForm.patchValue({ dateHourEndTeoric: null });
      }
      
      const loadDate = this.tripForm.get('loadDateTime')?.value;
      if (loadDate) {
        const loadDateTime = new Date(loadDate);
        if (loadDateTime < this.minDateLoad || loadDateTime > this.minDateEnd) {
          this.tripForm.patchValue({ loadDateTime: null });
        }
      }
    }
  }

  saveChanges() {
    if (!this.canEditBasicFields && !this.canEditStatus && !this.canEditUser) {
      this.snackBar.open(
        "No se pueden realizar modificaciones en el estado actual del viaje",
        "Cerrar",
        { duration: 3000 }
      );
      return;
    }

    if (this.tripForm.invalid) {
      this.snackBar.open(
        "Por favor complete todos los campos requeridos",
        "Cerrar",
        { duration: 3000 }
      );
      return;
    }

    this.updateTrip();
  }

  private updateTrip() {
    this.loading = true;
    
    const formValues = this.tripForm.value;
    
    let loadDateTimeFormatted = null;
    if (formValues.loadDateTime && formValues.time) {
      const combinedDateTime = DateTimeHelper.combineDateAndTime(formValues.loadDateTime, formValues.time);
      
      if (combinedDateTime) {
        loadDateTimeFormatted = DateTimeHelper.toUTCForBackend(combinedDateTime);
        
      }
    }

    const dataToSend: IUpdateTrip = {};

    if (this.canEditBasicFields) {
      dataToSend.description = formValues.description;
      
      if (loadDateTimeFormatted) {
        dataToSend.loadDateTime = new Date(loadDateTimeFormatted);
      } else {
        dataToSend.loadDateTime = null;
      }
      
      dataToSend.dateHourStartTeoric = formValues.dateHourStartTeoric
        ? new Date(this.toUTCISODate(new Date(formValues.dateHourStartTeoric), true))
        : null;
      dataToSend.dateHourEndTeoric = formValues.dateHourEndTeoric
        ? new Date(this.toUTCISODate(new Date(formValues.dateHourEndTeoric), false))
        : null;
      dataToSend.vehicleId = formValues.vehicleId;
    }

    if (this.canEditUser) {
      dataToSend.masterUserId = formValues.masterUserId;
    }

    if (this.canEditStatus) {
      dataToSend.statusTripId = formValues.statusTripId;
    }


    this.tripService.updateTrip(dataToSend, this.data.tripId).subscribe({
      next: (res) => {
        if (res?.statusCode == "200") {
          this.snackBar.open(
            "Se ha modificado el viaje exitosamente",
            "Cerrar",
            { duration: 3000 }
          );
          this.tripUpdated.emit();
          this.dialogRef.close(true);
        }
      },
      error: (err) => {
        this.snackBar.open(
          err.error.message || "Error al modificar el viaje",
          "Cerrar",
          { duration: 7000 }
        );
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  private toUTCISODate(date: Date, isStartOfDay: boolean): string {
    const localDate = new Date(date);

    if (isStartOfDay) {
      localDate.setHours(0, 0, 0, 0);
    } else {
      localDate.setHours(23, 59, 59, 999);
    }

    return localDate.toISOString();
  }

  onCancel() {
    this.dialogRef.close(false);
  }



}